import React, { useState } from 'react'
import { Card, Row, Col, Avatar, Button, Typography, Space, Checkbox, Alert, theme, notification } from 'antd'
import {
  UserOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  VideoCameraOutlined
} from '@ant-design/icons'
import { bookAppointment } from '../../api/searchApi'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { createBooking } from '../../../Bookings/utils/utils'
import { executeBooking, registerBooking } from '../../../Bookings/utils/bpm-utils'
import { generateOTP } from '../../../lib/utils'

const { Title, Text } = Typography

const AppointmentConfirmationMobile = ({
  doctor,
  selectedSlot,
  selectedReason,
  onBack,
  onConfirm,
  isVirtualVisit = false,
  searchFilters = {}
}) => {
  const { token } = theme.useToken()
  const [agreedToTerms, setAgreedToTerms] = useState(false) // Revert to unchecked since we have modal confirmation
  const [loading, setLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [bookingDetails, setBookingDetails] = useState(null)
  const authState = useSelector((state) => state.auth)

  // Determine if this is a virtual consultation
  const isVirtual =
    isVirtualVisit ||
    searchFilters.consultType?.includes('virtual') ||
    searchFilters.searchType === 'virtual_consultation'

  console.log('Virtual Consultation Detection:', {
    isVirtualVisit,
    consultType: searchFilters.consultType,
    searchType: searchFilters.searchType,
    isVirtual,
    searchFilters
  })

  const formatDate = (date) => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ]

    const day = dayNames[date.getDay()]
    const dayNum = date.getDate()
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()

    return `${day}, ${dayNum} ${month} ${year}`
  }

  const handleConfirmBooking = async () => {
    if (!agreedToTerms) {
      notification.warning({
        message: 'Terms Required',
        description: 'Please agree to the terms and conditions to proceed.',
        duration: 3
      })
      return
    }
    let processRecord = null
    const bpmKey = 'patient_journey'
    const processUser = `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`
    setLoading(true)
    try {
      const firstStep = doctor.clinicRecord.bpmConfig?.[0]?.key || 'check_in'
      processRecord = await registerBooking(bpmKey, processUser)

      // Determine the appropriate step based on consultation type
      let targetStep = firstStep
      if (isVirtual) {
        // For virtual consultations, skip check-in and go directly to consultation
        targetStep = 'consultation'
      }

      processRecord = await executeBooking(
        bpmKey,
        processRecord.recordId,
        'submit',
        processUser,
        processRecord.key,
        targetStep
      )
      // Normalize the date to 02:00:00
      const normalizedDate = dayjs(selectedSlot.date).hour(2).minute(0).second(0).millisecond(0).toDate()
      const otp = generateOTP(6)
      const newBooking = await createBooking({
        userRef: authState.agiliteUser._id,
        otp,
        status: processRecord.key,
        clinicRecord: doctor.clinicRecord,
        medicalProfRef: doctor._id,
        bookingDate: normalizedDate,
        reason: selectedReason,
        chiefComplaint: selectedReason,
        processRef: processRecord.recordId,
        clinicRef: doctor.clinicRecord._id,
        startTime: selectedSlot.time,
        endTime: dayjs(selectedSlot.time, 'HH:mm').add(15, 'minutes').format('HH:mm'),
        isVirtualVisit: isVirtual // Use the determined virtual flag
      })

      // Store booking details for success screen
      setBookingDetails({
        ...newBooking,
        doctorName: `${doctor?.firstName} ${doctor?.lastName}`.trim(),
        date: normalizedDate,
        time: selectedSlot.time,
        reason: selectedReason,
        location: isVirtual ? 'Virtual Consultation' : doctor.clinicRecord?.name || 'Flexi Facility',
        address: isVirtual ? 'Online Video Call' : doctor.clinicRecord?.address || 'Milnerton, Cape Town, South Africa',
        isVirtual: isVirtual
      })
      setShowSuccess(true)
    } catch (error) {
      console.error('Error confirming booking:', error)
      notification.error({
        message: 'Booking Failed',
        description: 'Unable to confirm your appointment. Please try again.',
        duration: 4
      })
    } finally {
      setLoading(false)
    }
  }

  // Success Screen for Mobile
  if (showSuccess) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
        {/* Success Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '48px 24px',
            textAlign: 'center',
            color: 'white',
            borderRadius: '0 0 24px 24px',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)'
          }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              animation: 'pulse 2s ease-in-out infinite'
            }}
          >
            <CheckCircleOutlined style={{ fontSize: '40px', color: 'white' }} />
          </div>

          <Title level={3} style={{ color: 'white', margin: '0 0 12px 0', fontWeight: '600', fontSize: '24px' }}>
            Appointment Confirmed!
          </Title>
          <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
            Your appointment has been successfully booked
          </Text>
        </div>

        {/* Appointment Details Card */}
        <div style={{ padding: '24px 16px' }}>
          <div className='mobile-confirmation-container'>
            <Card
              style={{
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                marginBottom: '20px'
              }}
            >
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <CalendarOutlined style={{ color: token.colorPrimary, fontSize: '20px' }} />
                  <Title level={4} style={{ margin: 0, fontSize: '18px' }}>
                    Appointment Details
                  </Title>
                </div>

                <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                  {/* Doctor Info */}
                  <div style={{ paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
                    <Text style={{ color: '#6b7280', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                      Doctor
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Avatar size={48} src={doctor.image} icon={<UserOutlined />} />
                      <div>
                        <Text strong style={{ fontSize: '16px', display: 'block' }}>
                          {bookingDetails?.doctorName}
                        </Text>
                        <Text style={{ color: '#6b7280', fontSize: '14px' }}>
                          {doctor.extraData?.profession || 'Medical Practitioner'}
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div style={{ paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
                    <Text style={{ color: '#6b7280', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                      Date & Time
                    </Text>
                    <Text strong style={{ fontSize: '16px' }}>
                      {bookingDetails && formatDate(bookingDetails.date)}
                    </Text>
                    <Text style={{ display: 'block', fontSize: '14px', marginTop: '4px' }}>
                      <ClockCircleOutlined style={{ marginRight: '6px' }} />
                      {bookingDetails?.time}
                    </Text>
                  </div>

                  {/* Location */}
                  <div style={{ paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
                    <Text style={{ color: '#6b7280', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                      {bookingDetails?.isVirtual ? 'Consultation Type' : 'Location'}
                    </Text>
                    <Text strong style={{ fontSize: '16px', display: 'block' }}>
                      {bookingDetails?.isVirtual ? (
                        <VideoCameraOutlined style={{ marginRight: '6px', color: token.colorPrimary }} />
                      ) : (
                        <EnvironmentOutlined style={{ marginRight: '6px', color: token.colorPrimary }} />
                      )}
                      {bookingDetails?.location}
                    </Text>
                    <Text style={{ color: '#6b7280', fontSize: '14px', marginLeft: '20px' }}>
                      {bookingDetails?.address}
                    </Text>
                  </div>

                  {/* Reason */}
                  <div>
                    <Text style={{ color: '#6b7280', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                      Reason for Visit
                    </Text>
                    <Text strong style={{ fontSize: '16px' }}>
                      {bookingDetails?.reason}
                    </Text>
                  </div>

                  {/* Booking Reference */}
                  {bookingDetails?._id && (
                    <div style={{ marginTop: '12px', padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Text style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                        Booking Reference
                      </Text>
                      <Text style={{ fontFamily: 'monospace', fontSize: '14px', color: '#374151' }}>
                        {bookingDetails._id}
                      </Text>
                    </div>
                  )}
                </Space>
              </div>
            </Card>

            {/* Reminders */}
            <Alert
              type='info'
              showIcon
              message='Important Reminders'
              description={
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                  <li>Arrive 15 minutes early</li>
                  <li>Bring your ID and medical aid card</li>
                  <li>Check your email for confirmation</li>
                </ul>
              }
              style={{ borderRadius: '12px', marginBottom: '24px' }}
            />

            {/* Done Button */}
            <Button
              type='primary'
              size='large'
              block
              onClick={() => onConfirm?.(bookingDetails)}
              style={{
                height: '48px',
                borderRadius: '8px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
            >
              Done
            </Button>
          </div>
        </div>

        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.05); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    )
  }

  return (
    <div style={{ padding: '16px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div className='mobile-confirmation-container'>
        {/* Header Card */}
        <Card
          style={{
            marginBottom: '16px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}
          bodyStyle={{ padding: '24px', textAlign: 'center' }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              border: '2px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <CheckCircleOutlined style={{ fontSize: '28px', color: 'white' }} />
          </div>

          <Title level={3} style={{ color: 'white', margin: '0 0 8px 0', fontSize: '20px' }}>
            Confirm Your Appointment
          </Title>
          <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
            Please review your appointment details and agree to our terms
          </Text>
        </Card>

        {/* Doctor Information Card */}
        <Card
          style={{ marginBottom: '16px', borderRadius: '12px' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontSize: '16px' }}>Doctor Information</span>
            </div>
          }
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.1)'
            }}
          >
            <Row align='middle' gutter={16}>
              <Col>
                <Avatar
                  size={56}
                  src={doctor.image}
                  icon={<UserOutlined />}
                  style={{ border: `2px solid ${token.colorPrimary}` }}
                />
              </Col>
              <Col flex={1}>
                <Title level={5} style={{ margin: '0 0 4px 0', fontSize: '16px' }}>
                  {doctor?.firstName} {doctor?.lastName}
                </Title>
                <Text style={{ color: '#666', fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                  {doctor.extraData?.profession || 'Medical Practitioner'}
                </Text>
                <Text style={{ color: token.colorPrimary, fontSize: '12px', fontWeight: '500' }}>
                  {doctor.extraData?.qualification || 'MBChB'}
                </Text>
              </Col>
            </Row>

            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(59, 130, 246, 0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                {isVirtual ? (
                  <VideoCameraOutlined style={{ color: token.colorPrimary, fontSize: '14px' }} />
                ) : (
                  <EnvironmentOutlined style={{ color: token.colorPrimary, fontSize: '14px' }} />
                )}
                <Text strong style={{ fontSize: '14px' }}>
                  {isVirtual ? 'Virtual Consultation' : doctor.clinicRecord?.name || 'Flexi Facility'}
                </Text>
              </div>
              <Text style={{ color: '#666', fontSize: '13px', marginLeft: '22px' }}>
                {isVirtual ? 'Online Video Call' : doctor.clinicRecord?.address || 'Milnerton, Cape Town, South Africa'}
              </Text>
            </div>
          </div>
        </Card>

        {/* Appointment Details Card */}
        <Card
          style={{ marginBottom: '16px', borderRadius: '12px' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CalendarOutlined style={{ color: token.colorPrimary }} />
                <span style={{ fontSize: '16px' }}>Appointment Details</span>
              </div>
              {isVirtual && (
                <div
                  style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: '#10b981',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <VideoCameraOutlined style={{ fontSize: '12px' }} />
                  Virtual
                </div>
              )}
            </div>
          }
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #fefefe 0%, #f9fafb 100%)',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}
          >
            <Row gutter={[0, 12]}>
              <Col span={24}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ color: '#374151', fontSize: '14px' }}>
                    Date:
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#6b7280' }}>{formatDate(selectedSlot.date)}</Text>
                </div>
              </Col>
              <Col span={24}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ color: '#374151', fontSize: '14px' }}>
                    Time:
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ClockCircleOutlined style={{ color: token.colorPrimary, fontSize: '12px' }} />
                    <Text style={{ fontSize: '14px', color: '#6b7280' }}>{selectedSlot.time}</Text>
                    <Button
                      type='link'
                      size='small'
                      onClick={onBack}
                      style={{ padding: '0 4px', height: 'auto', fontSize: '12px' }}
                    >
                      Change
                    </Button>
                  </div>
                </div>
              </Col>
              <Col span={24}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Text strong style={{ color: '#374151', fontSize: '14px' }}>
                    Reason:
                  </Text>
                  <Text
                    style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      textAlign: 'right',
                      flex: 1,
                      marginLeft: '16px'
                    }}
                  >
                    {selectedReason}
                  </Text>
                </div>
              </Col>
            </Row>
          </div>
        </Card>

        {/* Terms and Conditions Card */}
        <Card style={{ marginBottom: '16px', borderRadius: '12px' }}>
          <Title level={5} style={{ marginBottom: '16px', fontSize: '16px' }}>
            Terms and Conditions
          </Title>

          <div
            style={{
              background: '#fafafa',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              maxHeight: '120px',
              overflowY: 'auto',
              border: '1px solid #e5e7eb'
            }}
          >
            <ul style={{ paddingLeft: '16px', margin: 0, fontSize: '13px', color: '#4b5563', lineHeight: '1.5' }}>
              <li style={{ marginBottom: '8px' }}>Please arrive 15 minutes before your scheduled appointment</li>
              <li style={{ marginBottom: '8px' }}>Bring valid ID and health insurance card</li>
              <li style={{ marginBottom: '8px' }}>24-hour notice required for cancellations</li>
              <li style={{ marginBottom: '8px' }}>Personal information will be kept confidential</li>
              <li>Payment due at time of service</li>
            </ul>
          </div>

          <div
            style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              padding: '12px',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.1)'
            }}
          >
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              style={{ fontSize: '14px' }}
            >
              <Text style={{ color: '#1f2937', fontSize: '14px' }}>I agree to the terms and conditions</Text>
            </Checkbox>
          </div>
        </Card>

        {/* Alert for terms */}
        {!agreedToTerms && (
          <Alert
            message='Please agree to the terms and conditions to proceed'
            type='warning'
            showIcon
            style={{ marginBottom: '16px', borderRadius: '8px' }}
          />
        )}

        {/* Action Buttons */}
        <div className='mobile-action-buttons'>
          <Button
            size='large'
            onClick={onBack}
            style={{
              flex: 1,
              height: '48px',
              borderRadius: '8px',
              fontWeight: '500',
              border: '1px solid #d1d5db'
            }}
          >
            Back
          </Button>
          <Button
            type='primary'
            size='large'
            loading={loading}
            disabled={!agreedToTerms}
            onClick={handleConfirmBooking}
            style={{
              flex: 2,
              height: '48px',
              borderRadius: '8px',
              fontWeight: '600',
              background: agreedToTerms ? 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)' : undefined,
              border: 'none'
            }}
          >
            Confirm Appointment
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AppointmentConfirmationMobile
